import { useEffect, useId, useState } from "react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Switch } from "components/ui/switch";
import { cn } from "utils";

import {
  SHARING_DEFAULTS,
  type SharingChoice,
  type UserProfile,
} from "./schemas";
import { useSaveSharing } from "./use-user-profile";

/**
 * The two contact details a person may show on their attendee card.
 *
 * LinkedIn is shown unless switched off, which is how the directory behaved
 * before this choice existed; email is hidden unless switched on. Each switch
 * saves at once. The server applies the choice when it builds the directory,
 * so a hidden link or address never reaches another attendee's browser.
 */
export function SharingSettingsCard({
  uid,
  profile,
  className,
}: {
  uid: string;
  profile: UserProfile;
  className?: string;
}) {
  const save = useSaveSharing(uid);
  // What this card has saved, or is saving, since `profile` was loaded. The
  // switches show the profile otherwise, so a reload of the profile is what
  // they show; a new profile object drops the overrides.
  const [overrides, setOverrides] = useState<
    Partial<Record<SharingChoice, boolean>>
  >({});
  const [pending, setPending] = useState<SharingChoice | null>(null);
  useEffect(() => setOverrides({}), [profile]);

  const value = (choice: SharingChoice): boolean =>
    overrides[choice] ?? profile[choice] ?? SHARING_DEFAULTS[choice];

  const change = async (choice: SharingChoice, next: boolean) => {
    const previous = overrides[choice];
    setOverrides((current) => ({ ...current, [choice]: next }));
    setPending(choice);
    const failure = await save(choice, next);
    setPending(null);
    if (failure) {
      setOverrides((current) => ({ ...current, [choice]: previous }));
      toast.error(`Could not save: ${failure}`);
      return;
    }
    toast.success("Sharing updated");
  };

  // The roster row, which is what the directory publishes, holds the address
  // normalised the way registration and the roster claim normalise it.
  const publishedEmail = profile.email.trim().toLowerCase();

  return (
    <Card className={className} data-testid="sharing-settings">
      <CardHeader>
        <CardTitle>What other attendees see</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <SharingRow
          label="Show my LinkedIn to other attendees"
          hint="Also hides a LinkedIn link an organiser added for you."
          checked={value("showLinkedin")}
          disabled={pending !== null}
          onChange={(next) => void change("showLinkedin", next)}
          testId="share-linkedin"
        />
        <SharingRow
          label="Show my email to other attendees"
          hint={`Your sign-in address, ${publishedEmail}`}
          checked={value("showEmail")}
          disabled={pending !== null}
          onChange={(next) => void change("showEmail", next)}
          testId="share-email"
        />
      </CardContent>
    </Card>
  );
}

function SharingRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}) {
  const id = useId();
  return (
    // The whole row is the label, so the tap target is the row (at least
    // 44px) and not the 24px switch alone.
    <label
      htmlFor={id}
      className={cn(
        "flex min-h-11 cursor-pointer items-center justify-between gap-4 rounded-md py-2",
        disabled && "cursor-wait",
      )}
      data-testid={`${testId}-row`}
    >
      <span className="min-w-0 space-y-0.5">
        <span className="block text-sm font-medium text-foreground">
          {label}
        </span>
        <span className="block text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {hint}
        </span>
      </span>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        data-testid={testId}
      />
    </label>
  );
}
